import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {IUserSettings, IUserSettingsUpdate} from '@models/user.interface';
import {NotificationService} from '@services/frontend/notification.service';
import {AuthService} from '@services/auth.service';
import {HttpErrorResponse} from '@angular/common/http';
import {UserService} from '@services/user.service';
import {ExecutiveSubscriptionState} from '@services/executive-subscription.state';

@Component({
  selector: 'app-company-parametres',
  imports: [FormsModule],
  templateUrl: './company-parametres.html'
})
export class CompanyParametres implements OnInit {
  private readonly userService = inject(UserService);
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  protected readonly executiveSubscriptionState = inject(ExecutiveSubscriptionState);

  loading: WritableSignal<boolean> = signal(false);
  settings: WritableSignal<IUserSettings | null> = signal(null);
  language = signal('Français');
  timezone = signal('Europe/Paris (UTC+2)');
  showPasswordForm = signal(false);
  currentPassword = signal('');
  newPassword = signal('');
  confirmNewPassword = signal('');
  changingPassword = signal(false);

  private readonly passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  ngOnInit(): void {
    this.executiveSubscriptionState.ensureLoaded();
    this.loadSettings();
  }

  loadSettings(): void {
    this.loading.set(true);
    this.userService.getMySettings().subscribe({
      next: (value) => {
        this.settings.set(value);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  saveContact(): void {
    const currentSettings = this.settings();
    if (!currentSettings) return;
    const payload: IUserSettingsUpdate = {
      contactName: currentSettings.contactName,
      contactMail: currentSettings.contactMail,
      cabinetName: currentSettings.cabinetName ?? null,
      cabinetPhone: currentSettings.cabinetPhone ?? null,
      cabinetEmail: currentSettings.cabinetEmail ?? null,
      legalAlertsEnabled: currentSettings.legalAlertsEnabled,
      expiryAlertsEnabled: currentSettings.expiryAlertsEnabled,
      emailNotificationsEnabled: currentSettings.emailNotificationsEnabled,
      companyNotifications: currentSettings.companyNotifications.map((entry) => ({siret: entry.siret, enabled: entry.enabled})),
    };
    this.userService.updateMySettings(payload).subscribe({
      next: (value) => {
        this.settings.set(value);
        this.notificationService.success('Contact enregistré', 2000);
      },
      error: () => this.notificationService.error('Impossible d’enregistrer le contact', 2500),
    });
  }

  notifyPreferenceUpdated(): void {
    this.notificationService.success('Préférence mise à jour', 1800);
  }

  toggleSetting(key: keyof Pick<IUserSettings, 'legalAlertsEnabled' | 'expiryAlertsEnabled' | 'emailNotificationsEnabled'>): void {
    this.settings.update((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        [key]: !current[key]
      };
    });
    this.notifyPreferenceUpdated();
  }

  toggleCompanyNotification(siret: string): void {
    this.settings.update((current) => {
      if (!current) return current;
      return {
        ...current,
        companyNotifications: current.companyNotifications.map((entry) =>
          entry.siret === siret ? {...entry, enabled: !entry.enabled} : entry
        ),
      };
    });
    this.notifyPreferenceUpdated();
  }

  togglePasswordForm(): void {
    this.showPasswordForm.update((v) => !v);
    if (!this.showPasswordForm()) {
      this.currentPassword.set('');
      this.newPassword.set('');
      this.confirmNewPassword.set('');
    }
  }

  canSubmitPasswordChange(): boolean {
    return this.currentPassword().trim().length > 0
      && this.passwordRegex.test(this.newPassword())
      && this.newPassword() === this.confirmNewPassword();
  }

  savePassword(): void {
    if (!this.canSubmitPasswordChange()) {
      this.notificationService.error('Veuillez vérifier le formulaire de mot de passe', 2500);
      return;
    }

    this.changingPassword.set(true);
    this.authService.changeMyPassword({
      currentPassword: this.currentPassword(),
      newPassword: this.newPassword()
    }).subscribe({
      next: () => {
        this.changingPassword.set(false);
        this.notificationService.success('Mot de passe modifié avec succès', 2000);
        this.currentPassword.set('');
        this.newPassword.set('');
        this.confirmNewPassword.set('');
        this.showPasswordForm.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.changingPassword.set(false);
        const message = typeof err.error === 'string' && err.error.length > 0
          ? err.error
          : 'Impossible de modifier le mot de passe';
        this.notificationService.error(message, 3000);
      }
    });
  }
}
